import { Signal, WritableSignal, effect, untracked } from '@angular/core';
import {
  DocumentAttachment,
  type DocumentUploadValue,
} from '@sportello-balneari/shared-components';
import { concat, map, Observable, of, switchMap } from 'rxjs';
import { CompilingApiService } from '../../api/compiling-api.service';
import { TenderFormAnswerAttachment } from '@sportello-balneari/api-client';

export const uploadFn =
  (compilingApi: CompilingApiService, envelope: string) =>
  (category: string) =>
  (file: File) =>
    compilingApi
      .uploadFile({
        attachment: file,
      })
      .pipe(
        map(
          (response) =>
            ({
              id: crypto.randomUUID(),
              fileName: file.name,
              url: response.downloadUrl,
              metadata: {
                blobName: response.blobName,
                category,
                envelope,
              },
            }) as DocumentAttachment,
        ),
      );

/**
 * The attachment as the upload/reindex call returned it, then — while it is `InProgress` — every
 * state `watchIndexing` reads, so the badge follows the background indexer to its verdict. The
 * calls only queue the indexing: it runs through Content Understanding (images and scans included),
 * which can take minutes.
 */
function followIndexing(
  compilingApi: CompilingApiService,
  response: TenderFormAnswerAttachment,
  toAttachment: (response: TenderFormAnswerAttachment) => DocumentAttachment,
): Observable<DocumentAttachment> {
  const first = toAttachment(response);
  return first.indexingStatus === 'InProgress'
    ? concat(
        of(first),
        compilingApi.watchIndexing(first.id).pipe(map(toAttachment)),
      )
    : of(first);
}

export const uploadAndIndexFn =
  (
    compilingApi: CompilingApiService,
    envelope: string,
    getTenderFormAnswerId: () => string,
  ) =>
  (category: string) =>
  (file: File) =>
    compilingApi
      .uploadAndIndex({
        file,
        tenderFormAnswerId: getTenderFormAnswerId(),
        envelope,
        category,
      })
      .pipe(
        switchMap((uploaded) =>
          followIndexing(compilingApi, uploaded, (response) => ({
            id: response.id,
            fileName: response.fileName,
            url: response.downloadUrl,
            indexingStatus: response.indexingStatus,
            indexingError: response.indexingError,
            metadata: {
              blobName: response.blobName,
              category: response.category,
              envelope: response.envelope,
            },
          })),
        ),
      );

export const deleteAttachmentFn =
  (compilingApi: CompilingApiService) => (file: DocumentAttachment) =>
    compilingApi.deleteAttachment(file.id);

export const reindexAttachmentFn =
  (compilingApi: CompilingApiService) => (file: DocumentAttachment) =>
    compilingApi.reindexAttachment(file.id).pipe(
      switchMap((reindexed) =>
        followIndexing(compilingApi, reindexed, (response) => ({
          id: response.id,
          fileName: response.fileName,
          url: response.downloadUrl,
          indexingStatus: response.indexingStatus,
          indexingError: response.indexingError,
          metadata: {
            // Spread first: reindexing re-reads the file, it does not move it. A detached
            // evaluation's reindex sends the blob fields empty, so rebuilding metadata from the
            // response alone would drop the blob name — harmless where attachments are
            // server-side rows already, but detached evaluations re-send this metadata on every
            // save, so the file would be persisted without it.
            ...file.metadata,
            category: response.category,
            envelope: response.envelope,
          },
        })),
      ),
    );

export function migrateUnknownAttachments(
  attachments: WritableSignal<DocumentUploadValue>,
  knownSlotIds: Signal<Set<string>>,
): void {
  effect(() => {
    const v = attachments();
    const unknownKeys = Object.keys(v).filter((k) => !knownSlotIds().has(k));
    if (unknownKeys.length === 0) return;
    untracked(() =>
      attachments.update((current) => {
        const next = { ...current };
        for (const key of unknownKeys) {
          next['altri-documenti'] = [
            ...(next['altri-documenti'] ?? []),
            ...next[key],
          ];
          delete next[key];
        }
        return next;
      }),
    );
  });
}
